import { useQuery } from '@apollo/client';
import {
  ChatConversationStatus,
  HostInboxDocument,
  HostInboxQuery,
  UserInboxDocument,
  UserInboxQuery,
} from '@website/types/graphqlOperations';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import Link from 'next/link';
import { useRouter } from 'next/router';
import {
  Alert,
  Badge,
  Button,
  Col,
  ListGroup,
  ListGroupItem,
  Row,
} from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroll-component';
import ErrorPage from '../utilities/ErrorPage';
import LoadingPage from '../utilities/LoadingPage';
import OfflineAlert from '../utilities/OfflineAlert';
import styles from './inbox.module.scss';
import FilterInboxByStatus from './utils/FilterInboxByStatus';
import getParticipantsPicturesExcludingSelf from './utils/getParticipantsPicturesExcludingSelf';

/**
 * Inbox conversation card
 */
export function InboxConversationCard({
  conversation,
  inboxParticipantID,
}: {
  conversation: UserInboxQuery['user']['chatInbox']['items'][0];
  inboxParticipantID: string;
}) {
  const { asPath, query } = useRouter();
  const conversationStatus = query.status as ChatConversationStatus;

  const urlToConversation = `${asPath.split('?')[0]}/${conversation.id}${
    conversationStatus ? `?status=${ChatConversationStatus.OPEN}` : ''
  }`;

  const latestMessage = conversation.messages.items[0];

  const date = conversation.lastMessage
    ? DateTime.fromISO(conversation.lastMessage).toLocaleString(
        DateTime.DATETIME_MED,
      )
    : '';

  const isUnread =
    conversation.lastRead &&
    DateTime.fromISO(conversation.lastMessage) >
      DateTime.fromISO(conversation.lastRead);

  const participantsPictures = getParticipantsPicturesExcludingSelf(
    conversation.participants.items,
    inboxParticipantID,
  );

  return (
    <Link href={urlToConversation} legacyBehavior>
      <>
        <ListGroupItem
          className={clsx(
            `list-group-item-action`,
            isUnread ? 'bg-white fw-bold' : 'bg-light',
          )}
        >
          <Row className={clsx(styles.inboxRow)}>
            <Col xs="12" lg={3} className="align-self-center mb-lg-0">
              <div className={clsx(styles.singleLineText)}>
                {conversation.title ? (
                  <div
                    className="mb-0"
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}
                  >
                    {conversation.title}
                  </div>
                ) : (
                  <>
                    {participantsPictures.map((picture) => (
                      <Col
                        xs="auto"
                        className="d-flex align-items-center"
                        key={picture.name}
                      >
                        <span className="align-self-center text-center">
                          {picture.name}
                        </span>
                        {/*  <Avatar
                          image={picture.image}
                          alt="user"
                          size="xs"
                          className="avatar-border-white ms-2"
                          defaultImageText={picture.name}
                        /> */}
                      </Col>
                    ))}
                  </>
                )}
              </div>
              <Link href={urlToConversation} className="stretched-link"></Link>
            </Col>
            <Col
              xs="12"
              lg="6"
              className={clsx(
                styles.singleLineText,
                'd-flex align-items-center',
              )}
            >
              {conversation.id.startsWith('group_') ? (
                <span className="text-muted">Guest group chat</span>
              ) : (
                <>
                  {latestMessage?.text ? (
                    <>
                      {latestMessage?.text}
                      {isUnread && (
                        <Badge bg="success" className="ms-2 ">
                          <span className="small">New</span>
                        </Badge>
                      )}
                    </>
                  ) : (
                    <div className="text-muted text-sm fst-italic">(empty)</div>
                  )}
                </>
              )}

              <Link href={urlToConversation} className="stretched-link"></Link>
            </Col>
            <Col
              xs="12"
              lg={3}
              className="text-lg-end d-flex align-items-center justify-content-md-end"
            >
              <span className="text-muted text-sm">{date}</span>
            </Col>
          </Row>
        </ListGroupItem>
      </>
    </Link>
  );
}

/**
 * Inbox
 */
export default function Inbox({
  userID,
  hostURL,
}: {
  userID?: string;
  hostURL?: string;
}) {
  const { query, push } = useRouter();
  const conversationStatus = query.status as ChatConversationStatus;

  // Use a user or host query, depending on the location
  const userRes = useQuery(UserInboxDocument, {
    skip: !!hostURL,
    variables: { userID: userID || '' },
    pollInterval: 15000,
    fetchPolicy: 'cache-and-network',
  });
  const hostRes = useQuery(HostInboxDocument, {
    skip: !!userID,
    variables: {
      hostURL,
      chatStatus: conversationStatus || ChatConversationStatus.OPEN,
    },
    pollInterval: 15000,
    fetchPolicy: 'cache-and-network',
  });

  const { data, loading, error } = userID ? userRes : hostRes;

  if (!data && loading) return <LoadingPage />;

  // Note if this throws after we have data (e.g. on polling) we shouldn't show any errors
  if (!data && error) return <ErrorPage error={error} />;

  const inboxItems =
    (data as UserInboxQuery).user?.chatInbox.items ||
    (data as HostInboxQuery).hostByURL.chatInbox.items;

  const nextToken =
    (data as UserInboxQuery).user?.chatInbox.nextToken ||
    (data as HostInboxQuery).hostByURL?.chatInbox.nextToken;

  const fetchMoreChatMessages = async () => {
    await hostRes.fetchMore({
      variables: {
        nextToken,
      },
      // After various attempt trying to use typePolicies, which at a
      // cache level, they introduced various issues that I decided
      // for the time being to fallback on updateQuery, which
      // unfortunately will be deprecated in future and replaced by
      // typePolicies. Hopefully, the issues around polling and
      // pagination will have been fixed by then.
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }
        return {
          // ...prev,
          hostByURL: {
            id: prev.hostByURL.id,
            chatInbox: {
              nextToken: fetchMoreResult.hostByURL.chatInbox.nextToken,
              items: [
                ...prev.hostByURL.chatInbox.items,
                ...fetchMoreResult.hostByURL.chatInbox.items,
              ],
            },
          },
        };
      },
    });
  };

  const filterControls = hostURL ? (
    <FilterInboxByStatus
      onFilterByStatus={(status) =>
        push(`/hosts/dashboard/${hostURL}/messages?status=${status}`)
      }
      value={conversationStatus}
    />
  ) : null;

  // Handle no messages
  if (!inboxItems[0]) {
    return (
      <>
        <h5>Inbox</h5>
        {filterControls}

        <Alert variant="info" className="mt-2">
          <p>
            <strong>You don't have any messages yet.</strong>
          </p>
          {!hostURL && (
            <>
              <p>
                When you book your first trip, you'll be added to a chat here
                with everyone else on your trip.
              </p>
              <p>
                For now, if you need support you can contact us with the link
                below:
              </p>
              <Link href="/help/contact" legacyBehavior>
                <Button>Get Support</Button>
              </Link>
            </>
          )}
        </Alert>
      </>
    );
  }

  return (
    <>
      <h5>Inbox</h5>

      <OfflineAlert message="We'll download your latest messages when you reconnect to WiFi/3G." />

      {filterControls}
      <ListGroup>
        <InfiniteScroll
          dataLength={inboxItems.length} // This is important field to render the next data
          next={fetchMoreChatMessages}
          hasMore={!!nextToken}
          loader={<LoadingPage />}
        >
          {inboxItems.map((conversation) => (
            <InboxConversationCard
              conversation={conversation}
              key={conversation.id}
              inboxParticipantID={
                (data as UserInboxQuery).user?.id ||
                (data as HostInboxQuery).hostByURL?.id
              }
            />
          ))}
        </InfiniteScroll>
      </ListGroup>
    </>
  );
}
